import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Picture, {
  propTypes as PicturePropTypes,
} from '../../../components/01_atoms/DefaultPicture';
import { propTypes as linkPropTypes } from '../../../components/01_atoms/Link';
import useScreenSize from '../../../hooks/useScreenSize';
import Link from '../../Link';
import Button from '../../Button';
import ArrowDownIcon from '../../../public/static/icons/arrow-down.svg';
import TruckIcon from '../../../public/static/icons/truck.svg';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../utils/behaviorSettings';
import { scrollToElement } from '../../../utils/scroll-to';

import styles from './index.module.scss';

const BBHeroSantaLetter = ({
  imageFirst,
  imageSecond,
  imageMobile = null,
  heading,
  subheading = '',
  button,
  secondaryButton = null,
  behaviorSettings = null,
  uuid = null,
}) => {
  const componentRef = useRef(null);
  const [nextSiblingId, setNextSiblingId] = useState(null);
  const { width } = useScreenSize();
  const isDesktop = width >= 1200;
  const imageMobileResolved = imageMobile ?? imageFirst;

  useEffect(() => {
    if (componentRef.current) {
      const nextSibling = componentRef.current.nextElementSibling;
      if (nextSibling && nextSibling.id) {
        setNextSiblingId(nextSibling.id);
      }
    }
  }, []);

  const scrollToNextBlock = () => {
    if (nextSiblingId) {
      scrollToElement({ elementId: nextSiblingId });
    }
  };

  const classes = [
    'bb',
    'bb-hero',
    styles['bb-hero-santa-letter'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div
      className={classes.join(' ')}
      id={uuid}
      ref={componentRef}
      data-component="bb-hero-santa-letter"
    >
      <div className={`${styles['bb-hero-santa-letter__container']} container`}>
        {isDesktop && (
          <>
            <div className={styles['bb-hero-santa-letter__image-first']}>
              <Picture {...imageFirst} />
            </div>
            <div className={styles['bb-hero-santa-letter__image-second']}>
              <Picture {...imageSecond} />
            </div>
          </>
        )}
        <div className={styles['bb-hero-santa-letter__content']}>
          <h1 className={styles['bb-hero-santa-letter__heading']}>{heading}</h1>
          {!isDesktop && (
            <div className={styles['bb-hero-santa-letter__image-mobile']}>
              <Picture {...imageMobileResolved} />
            </div>
          )}
          <div className={styles['bb-hero-santa-letter__buttons']}>
            {button && button.nextLink && (
              <Link {...button.nextLink}>
                <Button type="xmas" isMobileBlock data-button-type="primary">
                  {button.label}
                </Button>
              </Link>
            )}
            {secondaryButton && secondaryButton.nextLink && (
              <Link {...secondaryButton.nextLink}>
                <Button
                  type="xmas"
                  data-button-type="secondary"
                  isTransparent
                  isOutlined
                  isMobileBlock
                >
                  {secondaryButton.label}
                </Button>
              </Link>
            )}
          </div>
          {subheading && (
            <div className={styles['bb-hero-santa-letter__subheading']}>
              <TruckIcon />
              {subheading}
            </div>
          )}
          {!isDesktop && (
            <button onClick={scrollToNextBlock} className={styles['bb-hero-santa-letter__next']}>
              <ArrowDownIcon />
            </button>
          )}
        </div>
      </div>
      <div className={styles['bb-hero-santa-letter__bottom']}></div>
    </div>
  );
};

BBHeroSantaLetter.propTypes = {
  imageFirst: PropTypes.shape(PicturePropTypes).isRequired,
  imageSecond: PropTypes.shape(PicturePropTypes).isRequired,
  heading: PropTypes.string.isRequired,
  button: PropTypes.shape({
    nextLink: PropTypes.shape(linkPropTypes),
    label: PropTypes.string,
  }).isRequired,
  secondaryButton: PropTypes.shape({
    nextLink: PropTypes.shape(linkPropTypes),
    label: PropTypes.string,
  }),
  imageMobile: PropTypes.shape(PicturePropTypes),
  subheading: PropTypes.string,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBHeroSantaLetter;
